// ./client/components/Reports/Graphs.js
import React from "react";
import { useAppContext } from "../../context/appContext";
import { calcOptionTotal } from "../../utils/calcOptionTotal";
import { numberToMoney } from "../../utils/money";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

function BusinessReports() {
  ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

  const { reports, prices } = useAppContext();

  const options = {
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    stacked: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "FreeLite Metrics",
      },
    },
    scales: {
      y: {
        type: "linear",
        display: true,
        position: "left",
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  const dayMilSecs = 1000 * 60 * 60 * 24;
  const cssQtyLine = "#4d93ff";
  const cssQtyDot = "#005be6";
  const cssValLine = "#00b063";
  const cssValDot = "#0f5132";
  const cssQtyLine1 = "#4d9300";
  const cssQtyDot1 = "#005b00";
  const cssValLine1 = "#00b000";
  const cssValDot1 = "#0f5100";
  // const intervalDaily = 0;
  const intervalWeekly = 1;
  const intervalMonthly = 2;
  // const intervalQuarterly = 3;
  // const typeEstimates = 1;
  // const typeCompleted = 2;
  // const typePayments = 3;
  const typeVeluxJobs = 5;

  const termLength = () => {
    const startDate = new Date(reports.startDate);
    const endDate = new Date(reports.endDate);
    const diffTime = endDate - startDate;

    return Math.ceil(diffTime / dayMilSecs);
  };

  const termDays = Array.from({ length: termLength() }, (_, i) => i + 1).map((day) => {
    const date = new Date(reports.startDate + "T00:00:00");
    date.setDate(date.getDate() + (day - 1));
    return date.toISOString();
  });

  // Gets Appointments or Payments and filters based on date
  const getRecords = () => {
    let records = [];
    let appts = reports.appointments;
    // If file payments report, return payments in date range
    if (reports.reportTypesId === 3) {
      records = reports.payments?.filter(
        (payment) =>
          payment.payment_date >= reports.startDate && payment.payment_date <= reports.endDate
      );
    }
    // Otherwise return appointments
    else if (reports.reportTypesId === 1) {
      // Return only estimates
      appts = appts?.filter((appt) => appt.appt_types_id === 1);
    }
    // Or return unmodified service appointments
    else {
      appts = appts?.filter((appt) => appt.appt_types_id === 2);
    }
    // Return appointments within report date range
    records = appts?.filter(
      (appt) => appt.appt_date >= reports.startDate && appt.appt_date <= reports.endDate
    );

    // Return the desired records to build the report
    return records?.filter((rec) => {
      const jobType = reports.jobs?.filter((job) => job.id === rec.core_job_data_id)[0]
        ?.job_type_id;
      // Filter based on job type
      if (reports.includeService && jobType === 1) {
        return true;
      }
      if (reports.includeInstall && jobType === 3) {
        return true;
      }
      if (reports.includeCommercial && jobType === 4) {
        return true;
      }
    });
  };

  // Calculate the total value of the job associated with each record
  const calcValue = (crntJob, crntOption) => {
    if (!crntJob || !crntOption) {
      console.log("crntJob:", crntJob);
      console.log("crntOption:", crntOption);
      return;
    }
    const jobItems = reports.jobItems.filter((item) => item.options_id === crntOption.id);
    const fees = reports.fees.filter((fee) => fee.options_id === crntOption.id);
    const discounts = reports.discounts.filter((dsct) => dsct.options_id === crntOption.id);

    const totals = calcOptionTotal({
      jobItems,
      discounts,
      fees,
      sales_tax: crntOption.sales_tax,
      job: crntJob,
      prices,
    });

    // console.log("totals:", totals.grandTotal);
    return totals.grandTotal;
  };

  // -------- The Main Data Compiling Function ---------
  const fillData = (records) => {
    let labels = [];
    let quantity = [];
    let value = [];
    let termQty = 0;
    let termVal = 0;
    let termNumber;

    console.log("records", records);

    // Loop through each day in the report and calculate
    termDays.forEach((day, i) => {
      const date = new Date(day);
      const dayAppts = records?.filter((record) => {
        return record.appt_date === day || record.payment_date === day;
      });

      // dayAppts?.length > 0 && console.log("dayAppts:", dayAppts);

      let dayQty = dayAppts?.length || 0;

      // Find job totals
      const dayVal = dayAppts?.reduce((ttl, currentAppt) => {
        const currentJob = reports.jobs?.filter(
          (job) => job.id === currentAppt.core_job_data_id
        )[0];
        const currentOptions = reports.options?.filter(
          (opt) => opt.core_job_data_id === currentAppt.core_job_data_id
        );
        const chosenOption = currentOptions.filter((opt) => opt.id === currentJob.options_id)[0];

        // Determine value for estimates with multiple options
        if (currentOptions.length > 1) {
          // If chosen option not present then take an average value
          if (!chosenOption) {
            let ttlVal = 0;
            let qty = 0; // keep track to remove custom price estimates
            currentOptions.forEach((opt) => {
              const val = calcValue(currentJob, opt);
              if (val) {
                qty = qty + 1;
                ttlVal = ttlVal + val;
              }
            });
            // If none of the estimates have a valid price
            if (qty === 0) {
              // console.log(
              //   `Not counting this multi-option appointment (${currentAppt.id})! Daily quantity: `,
              //   dayQty
              // );
              dayQty = dayQty - 1; // remove from day's quantity count
              return ttl; // don't change the total value
            }

            return ttl + ttlVal / qty;
          }
        }
        // Otherwise use single value for daily total
        const val = calcValue(currentJob, chosenOption || currentOptions[0]);
        // Don't count if custom price
        if (!val) {
          // console.log(
          //   `Not counting this appointment (${currentAppt.id})! Daily quantity: `,
          //   dayQty
          // );
          dayQty = dayQty - 1; // remove from day's quantity count
          return ttl; // don't change the total value
        }

        return ttl + val;
      }, 0);

      // Compile monthly data
      if (Number(reports.interval) === intervalMonthly) {
        termQty = termQty + dayQty;
        termVal = termVal + dayVal;
        // Start tracking for first month
        if (i === 0) {
          labels.push(date.toDateString().slice(4, -5));
          termNumber = date.getMonth();
        }
        // If it changes add and reset variables
        else if (date.getMonth() !== termNumber) {
          labels.push(date.toDateString().slice(4, -5));
          quantity.push(termQty);
          value.push(termVal);
          termQty = 0;
          termVal = 0;
          termNumber = date.getMonth();
        }
        // Add data from last month
        else if (i === termDays.length - 1) {
          quantity.push(termQty);
          value.push(termVal);
        }
      }

      // Or compile weekly data if desired
      else if (Number(reports.interval) === intervalWeekly) {
        termQty = termQty + dayQty;
        termVal = termVal + dayVal;
        // Start tracking for first week
        if (i === 0) {
          labels.push(date.toDateString().slice(0, -5));
        }
        // If week starts over
        else if (i % 7 === 0) {
          labels.push(date.toDateString().slice(0, -5));
          quantity.push(termQty);
          value.push(termVal);
          termQty = 0;
          termVal = 0;
        }
        // Add data from last week
        else if (i === termDays.length - 1) {
          quantity.push(termQty);
          value.push(termVal);
        }
      }

      // Otherwise return daily data
      else {
        labels.push(date.toDateString().slice(0, -5));
        quantity.push(dayQty);
        // console.log("dayVal:", dayVal)
        value.push(dayVal);
      }
    });

    return { labels, quantity, value };
  };

  const records = getRecords();
  let data, data1;
  if (reports.reportTypesId < 4) {
    data = fillData(records);
  }
  // Filter the records list down to only jobs with velux skylights
  else if (reports.reportTypesId === typeVeluxJobs) {
    records.filter(rec => {
      const currentJob = reports.jobs?.filter((job) => job.id === rec.core_job_data_id)[0];
      const currentOptions = reports.options?.filter(
        (opt) => opt.core_job_data_id === rec.core_job_data_id
      );
      const currentOption = currentOptions.filter((opt) => opt.id === currentJob.options_id)[0];
      const currentjobItems = reports.jobItems.filter((item) => item.options_id === currentOption.id);
      const veluxFrameIds = [5,6,7,9,10] // 5 reg sun tnl, 6 low-profile st, 7 impact st, 9 curbmount, 10 deckmount
      // test if any of the job items has a velux frame
      return currentjobItems.some(jobItems => veluxFrameIds.includes(jobItems.frame_types_id))
    })
    data1 = fillData(records);
  }

  // const data = fillData(getRecords());
  const totalQty = data?.quantity.reduce((a, c) => a + c, 0);
  const totalValue = data?.value.reduce((a, c) => a + c, 0);
  const dataLength = data?.labels?.length;

  const myData = {
    labels: data?.labels,
    datasets: [
      data?.quantity.length > 0 && {
        id: 1,
        label: `Quantity`,
        data: data?.quantity,
        borderColor: cssQtyLine,
        backgroundColor: cssQtyDot,
        yAxisID: "y",
        tension: 0.2,
      },
      data?.value.length > 0 && {
        id: 2,
        label: `Total Value`,
        data: data?.value,
        borderColor: cssValLine,
        backgroundColor: cssValDot,
        yAxisID: "y1",
        tension: 0.2,
      },
    ],
  };

  const veluxDataset = [
    data1?.quantity.length > 0 && {
      id: 1,
      label: `Quantity Without Velux`,
      data: data1?.quantity,
      borderColor: cssQtyLine1,
      backgroundColor: cssQtyDot1,
      yAxisID: "y",
      tension: 0.2,
    },
    data1?.value.length > 0 && {
      id: 2,
      label: `Total Value Without Velux`,
      data: data1?.value,
      borderColor: cssValLine1,
      backgroundColor: cssValDot1,
      yAxisID: "y1",
      tension: 0.2,
    },
  ];

  // Add the velux datasets if needed
  if (reports.reportTypesId === 5) {
    myData.datasets.push(veluxDataset);
  }
  return (
    <div className="whole">
      {console.log("data:", data)}
      {!data?.labels.length === 0 || !data?.quantity.length === 0 || !data?.value.length === 0 ? (
        <div>Loading...</div>
      ) : (
        <>
          {/* ----- Graphs ------ */}
          <Line key={data?.value[0]} options={options} data={myData} />
          {/* ----- Totals ----- */}
          <div className="form-row report-totals">
            <br />
            Total Quantity: {totalQty}
            <br />
            Terms: {dataLength} ({dataLength-1} complete terms and 1 partial)
            <br />
            Average Quantity per Term: {(totalQty / dataLength).toFixed(1)}
            <br />
            Total Value: {numberToMoney(totalValue)}
            <br />
            Average Value per Term: {numberToMoney(totalValue / dataLength)}
            <br />
            Total Profit/Expected Profit: (coming soon)
            <br />
            Average Profit: (coming soon)
            <br />
            Largest Job: (coming soon)
            <br />
            Most Profitable Job: (coming soon)
          </div>
        </>
      )}
    </div>
  );
}
export default BusinessReports;
